import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Chip, CircularProgress, Alert, TablePagination, Breadcrumbs, Button,
    TextField, InputAdornment, FormControl, Select, MenuItem, Menu, ListItemIcon,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Refresh, LocalGroceryStore, Search, MoreVert, Visibility, PersonAdd, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllGroceryBookings, updateGroceryBookingStatus, getActiveServiceLocations } from '../../../services/Api';

const GetAllGroceryBookings = () => {
    const navigate = useNavigate();

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    // Pagination & Search
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
    const [locationFilter, setLocationFilter] = useState('');
    const [locations, setLocations] = useState([]);

    // Menu logic
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuSelectedBooking, setMenuSelectedBooking] = useState(null);

    // Status update logic
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [editingBooking, setEditingBooking] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [statusUpdating, setStatusUpdating] = useState(false);

    const handleMenuOpen = (event, booking) => {
        setAnchorEl(event.currentTarget);
        setMenuSelectedBooking(booking);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuSelectedBooking(null);
    };

    const handleStatusMenuClick = () => {
        setEditingBooking(menuSelectedBooking);
        setNewStatus(menuSelectedBooking.status?.toLowerCase() || 'pending');
        setStatusDialogOpen(true);
        handleMenuClose();
    };

    const handleStatusDialogClose = () => {
        setStatusDialogOpen(false);
        setEditingBooking(null);
    };

    const handleUpdateStatus = async () => {
        if (!editingBooking) return;
        setStatusUpdating(true);
        try {
            const response = await updateGroceryBookingStatus(editingBooking.id || editingBooking._id, { status: newStatus });
            if (response.success || response.message) {
                setSuccessMsg('Booking status updated successfully');
                setTimeout(() => setSuccessMsg(null), 3000);
                fetchBookings();
            } else {
                setError(response?.message || 'Failed to update status');
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Error occurred while updating status');
        } finally {
            setStatusUpdating(false);
            handleStatusDialogClose();
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0);
        }, 1000);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const res = await getActiveServiceLocations();
                if (res.success) {
                    setLocations(res.data || []);
                }
            } catch (err) {
                console.error('Failed to fetch locations', err);
            }
        };
        fetchLocations();
    }, []);

    const fetchBookings = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllGroceryBookings(page + 1, rowsPerPage, debouncedSearch, statusFilter, paymentStatusFilter, locationFilter);
            if (response.success || response.data) {
                const src = response.data || response;
                let targetArray = [];

                if (Array.isArray(src)) targetArray = src;
                else if (src && typeof src === 'object') {
                    if (Array.isArray(src.bookings)) targetArray = src.bookings;
                    else if (Array.isArray(src.data)) targetArray = src.data;
                }

                setBookings(targetArray);
                const total = response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || targetArray.length || 0;
                setTotalRows(total);
            } else {
                setError(response.message || 'Failed to fetch bookings');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching bookings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, debouncedSearch, statusFilter, paymentStatusFilter, locationFilter]);



    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusColor = (status) => {
        const s = status?.toLowerCase();
        if (s === 'completed' || s === 'delivered') return { bgcolor: 'success.light', color: 'success.dark' };
        if (s === 'cancelled' || s === 'rejected') return { bgcolor: 'error.light', color: 'error.dark' };
        if (s === 'pending' || s === 'processing') return { bgcolor: 'warning.light', color: 'warning.dark' };
        return { bgcolor: 'grey.200', color: 'grey.800' };
    };

    const getPaymentStatusColor = (paymentStatus) => {
        const s = paymentStatus?.toLowerCase();
        if (s === 'completed' || s === 'success') return { bgcolor: 'success.light', color: 'success.dark' };
        if (s === 'failed' || s === 'rejected') return { bgcolor: 'error.light', color: 'error.dark' };
        if (s === 'pending') return { bgcolor: 'warning.light', color: 'warning.dark' };
        return { bgcolor: 'grey.200', color: 'grey.800' };
    };

    const getPaymentMethodColor = (method) => {
        const m = method?.toLowerCase();
        if (m === 'online') return { bgcolor: 'info.light', color: 'info.dark' };
        if (m === 'cod') return { bgcolor: 'warning.light', color: 'warning.dark' };
        return { bgcolor: 'grey.200', color: 'grey.800' };
    };

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Typography color="text.primary">Bookings</Typography>
                    <Typography color="text.primary">Grocery Bookings</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocalGroceryStore sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 300, display: 'flex', alignItems: 'center' }}>
                                Grocery Bookings
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage all grocery delivery orders efficiently
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={fetchBookings}
                        disabled={loading}
                        sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider' }}
                    >
                        <Refresh />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                            displayEmpty
                            sx={{ borderRadius: '20px', bgcolor: 'rgba(0,0,0,0.02)' }}
                        >
                            <MenuItem value="" disabled>Booking Status</MenuItem>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="confirmed">Confirmed</MenuItem>
                            <MenuItem value="processing">Processing</MenuItem>
                            <MenuItem value="out_for_delivery">Out for Delivery</MenuItem>
                            <MenuItem value="delivered">Delivered</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={paymentStatusFilter}
                            onChange={(e) => { setPaymentStatusFilter(e.target.value); setPage(0); }}
                            displayEmpty
                            sx={{ borderRadius: '20px', bgcolor: 'rgba(0,0,0,0.02)' }}
                        >
                            <MenuItem value="" disabled>Payment Status</MenuItem>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={locationFilter}
                            onChange={(e) => { setLocationFilter(e.target.value); setPage(0); }}
                            displayEmpty
                            sx={{ borderRadius: '20px', bgcolor: 'rgba(0,0,0,0.02)' }}
                        >
                            <MenuItem value="">All Locations</MenuItem>
                            {locations.map((loc) => (
                                <MenuItem key={loc.id} value={loc.id}>
                                    {loc.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        placeholder="Search..."
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            width: 180,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                                backgroundColor: 'rgba(0,0,0,0.02)',
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 4 }}>{successMsg}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Booking Number</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Vendor</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Booking Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Payment Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Payment Method</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Partner Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 10 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : bookings.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">No grocery bookings found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                bookings.map((booking, index) => (
                                    <TableRow key={booking.id || booking._id} hover>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{booking.bookingNumber || `#${booking.id}`}</TableCell>
                                        <TableCell>{booking.user?.name || '-'}</TableCell>
                                        <TableCell>{booking.groceryVendor?.shopName || booking.vendor?.shopName || '-'}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>₹{booking.grandTotal || '0'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={booking.status || 'Pending'}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    textTransform: 'capitalize',
                                                    ...getStatusColor(booking.status)
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={booking.paymentStatus || 'Pending'}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    textTransform: 'capitalize',
                                                    ...getPaymentStatusColor(booking.paymentStatus)
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={booking.paymentMethod || 'N/A'}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    textTransform: 'capitalize',
                                                    ...getPaymentMethodColor(booking.paymentMethod)
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: booking.partnerId ? 'success.main' : 'error.main' }}>
                                                {booking.partnerId ? 'Assigned' : 'Not Assigned'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, booking)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                elevation={1}
                PaperProps={{
                    sx: {
                        minWidth: 150,
                        border: '1px solid',
                        borderColor: 'divider',
                        mt: 1,
                    }
                }}
            >
                <MenuItem onClick={() => {
                    navigate(`/bookings/grocery/details/${menuSelectedBooking?.id || menuSelectedBooking?._id}`);
                    handleMenuClose();
                }}>
                    <ListItemIcon><Visibility fontSize="small" color="primary" /></ListItemIcon>
                    View Details
                </MenuItem>
                <MenuItem onClick={handleStatusMenuClick}>
                    <ListItemIcon><Edit fontSize="small" color="info" /></ListItemIcon>
                    Update Status
                </MenuItem>
                <MenuItem onClick={() => {
                    navigate(`/bookings/grocery/assign-partner/${menuSelectedBooking?.id || menuSelectedBooking?._id}`);
                    handleMenuClose();
                }}>
                    <ListItemIcon><PersonAdd fontSize="small" color="secondary" /></ListItemIcon>
                    Assign Partner
                </MenuItem>
            </Menu>

            {/* Update Status Dialog */}
            <Dialog open={statusDialogOpen} onClose={handleStatusDialogClose} maxWidth="xs" fullWidth>
                <DialogTitle>Update Booking Status</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Booking #{editingBooking?.id || editingBooking?._id}
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                            >
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="confirmed">Confirmed</MenuItem>
                                <MenuItem value="processing">Processing</MenuItem>
                                <MenuItem value="out_for_delivery">Out for Delivery</MenuItem>
                                <MenuItem value="delivered">Delivered</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleStatusDialogClose} color="inherit">Cancel</Button>
                    <Button onClick={handleUpdateStatus} variant="contained" disabled={statusUpdating}>
                        {statusUpdating ? <CircularProgress size={20} color="inherit" /> : 'Update Status'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default GetAllGroceryBookings;
